/**
 * lifecycleListeners.ts — page-lifecycle flush wiring for analytics
 * (STORY-009-003).
 *
 * Registers (once) the listeners that flush any pending analytics events when
 * the page is hiding, so events queued just before a navigation / tab-close
 * are not lost (AC#4):
 *   - `visibilitychange` → `document.visibilityState === "hidden"`: the
 *     reliable "page is going away" signal on mobile (backgrounding the tab).
 *   - `pagehide`: fires on bfcache eviction + unload.
 *
 * Both flush via the `"pagehide"` trigger, which uses `navigator.sendBeacon`
 * (it survives unload; a normal `fetch` would be cancelled). Beacons carry no
 * headers, so these flushes are anonymous by construction — see
 * `@/lib/analytics/queue`.
 *
 * Idempotent: calling `installAnalyticsLifecycleListeners()` more than once
 * (e.g. React 19 StrictMode double-invocation in dev, or a hot reload) wires
 * the listeners exactly once.
 */
import { flushNow } from "@/lib/analytics/queue";

/** Guard so the listeners are attached at most once per document. */
let installed = false;

/** Flush pending events using the unload-safe beacon transport. */
function flushOnHide(): void {
  void flushNow("pagehide");
}

function handleVisibilityChange(): void {
  if (document.visibilityState === "hidden") {
    flushOnHide();
  }
}

/**
 * Install the page-lifecycle flush listeners. Call ONCE at app bootstrap
 * (`main.tsx`). No-op outside a DOM context and on repeat calls.
 */
export function installAnalyticsLifecycleListeners(): void {
  if (installed || typeof document === "undefined") {
    return;
  }
  installed = true;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flushOnHide);
}

/**
 * Tear down the listeners + reset the install guard. FOR TESTS ONLY.
 */
export function __resetLifecycleListenersForTests(): void {
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", flushOnHide);
  }
  installed = false;
}
